import { AlertTriangle, RefreshCw } from 'lucide-react'
import { classifyError } from '@/lib/errorKind'

/**
 * Last-resort screen for a crash above the router — providers, auth bootstrap,
 * or the router itself. It cannot rely on routing, app chrome, or theme state,
 * so it renders standalone and offers a reload as the only recovery.
 */
export function AppCrashFallback({
  error,
  reset,
}: {
  error: Error
  reset: () => void
}): React.ReactElement {
  const classified = classifyError(error, 'crash')

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-6 dark:bg-gray-950">
      <div
        role="alert"
        className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white px-8 py-10 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <span
          aria-hidden
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-danger/10 text-danger dark:bg-danger/15"
        >
          <AlertTriangle className="h-8 w-8" strokeWidth={1.75} />
        </span>
        <h1 className="mt-5 text-xl font-semibold tracking-[-0.01em] text-gray-900 dark:text-gray-100">
          The app hit an unexpected error
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-gray-600 dark:text-gray-400">
          Reloading usually clears it. If it keeps happening, contact your administrator.
        </p>
        {classified.detail && (
          <p className="mx-auto mt-4 max-w-sm rounded-lg bg-gray-100/80 px-3 py-2 font-mono text-xs leading-relaxed break-words text-gray-600 dark:bg-gray-800/70 dark:text-gray-400">
            {classified.detail}
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-[background-color,scale] duration-150 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-[0.96]"
          >
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
            Reload the app
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex h-9 cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-[background-color,scale] duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 active:scale-[0.96] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  )
}

AppCrashFallback.displayName = 'AppCrashFallback'
